import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import TutorialPlayer from '@/components/tutorial/TutorialPlayer';
import WorkflowDiagram from '@/components/tutorial/WorkflowDiagram';
import AnimatedExamples from '@/components/tutorial/AnimatedExamples';
import { TUTORIAL_PATH, TutorialAudience } from '@/data/tutorial';

interface Props {
  audience?: TutorialAudience;
  lockAudience?: boolean;
  placement: string;
  /** Section wrapper background. */
  tone?: 'white' | 'canvas' | 'none';
  /** Show the Get started / Full guide buttons. */
  ctas?: boolean;
  /** Use the 30-second version of the player (homepage quick visitors). */
  quick?: boolean;
  /** Show the 4 animated "after you buy" examples. */
  examples?: boolean;
  id?: string;
  title?: string;
  subtitle?: string;
}

const HowItWorksSection: React.FC<Props> = ({
  audience = 'sub', lockAudience, placement, tone = 'white', ctas = true, quick = false, examples = true, id, title, subtitle,
}) => {
  const { lang, path } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';
  const bg = tone === 'white' ? 'bg-white py-16' : tone === 'canvas' ? 'bg-slate-50 py-16' : 'py-8';

  return (
    <section id={id} className={`scroll-mt-24 ${bg}`}>
      <div className={tone === 'none' ? '' : 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'}>
        <div className="max-w-3xl">
          <p className="text-xs font-black uppercase tracking-widest text-orange-600">
            {quick ? (es ? 'En 30 segundos' : 'In 30 seconds') : es ? 'Tutorial en video' : 'Video walkthrough'}
          </p>
          <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            {title || (es ? 'Cómo funciona GIGGONE' : 'How GIGGONE Works')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-slate-600">
            {subtitle ||
              (quick
                ? es
                  ? 'Explore proyectos, compre un lead o membresía, desbloquee los datos del cliente y llame. La versión completa de 2 minutos está en la guía.'
                  : 'Browse projects, buy a lead or membership, unlock the homeowner details and call. The full 2-minute version is in the guide.'
                : es
                ? 'En 2 minutos: crear su cuenta, verificar su correo, comprar un lead o una membresía, cómo funcionan los créditos, dónde aparecen los datos del cliente y cómo ganar el trabajo.'
                : 'In 2 minutes: create your account, verify your email, buy a lead or a membership, how credits work, where homeowner details appear and how to win the job.')}
          </p>
        </div>

        <TutorialPlayer defaultAudience={audience} lockAudience={lockAudience} quick={quick} placement={placement} className="mt-8" />

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <p className="mb-5 text-sm font-extrabold uppercase tracking-wide text-slate-500">
            {es ? 'El flujo completo' : 'The complete workflow'}
          </p>
          <WorkflowDiagram />
        </div>

        {examples && <AnimatedExamples className="mt-12" />}

        {ctas && (
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link
              to={profile ? path('/jobs') : `${path('/register')}?role=${audience === 'gc' ? 'gc' : 'subcontractor'}`}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-orange-500 px-6 text-sm font-bold text-white hover:bg-orange-600"
            >
              {profile ? (es ? 'Ver proyectos' : 'Browse leads') : es ? 'Crear cuenta gratis' : 'Create free account'}
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={path(TUTORIAL_PATH)}
              className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-6 text-sm font-bold text-slate-800 hover:bg-slate-50"
            >
              <BookOpen className="h-4 w-4" /> {es ? 'Guía completa paso a paso' : 'Full step-by-step guide'}
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default HowItWorksSection;
